import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import AuthImage from "../../../components/common/AuthImage";
import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import { STORE_EVALUATIONS_ADMIN_PAGE } from "../routes";
import { SUPERVISOR_HOME_PAGE } from "../../supervisor/routes";
import { isAdminFromToken } from "../../../utils/authToken";
import { getStoreEvaluationByIdApi } from "../storeEvaluationsApi";
import type { StoreEvaluation, StoreEvaluationAnswer } from "../storeEvaluationsApi";

import "./StoreEvaluationDetailPage.css";

type Question = NonNullable<StoreEvaluationAnswer["question"]>;

/** Faixas de nota — as mesmas da lista e do painel de Qualidade. */
const scoreBand = (score01: number) => (score01 >= 0.8 ? "good" : score01 >= 0.5 ? "warn" : "bad");

const hasValue = (v: unknown) => v !== null && v !== undefined;

const formatDate = (ymdOrIso?: string | null) => {
  if (!ymdOrIso) return "—";
  const [y, m, d] = ymdOrIso.split("T")[0].split("-");
  return y && m && d ? `${d}/${m}/${y}` : ymdOrIso;
};

const formatDateTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const formatShortDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const typeHint = (q: Question) => {
  switch (q.type) {
    case "SCALE":
      return hasValue(q.minValue) && hasValue(q.maxValue) ? `Escala de ${q.minValue} a ${q.maxValue}` : "Escala";
    case "SINGLE_CHOICE":
      return "Escolha única";
    case "MULTI_CHOICE":
      return "Múltipla escolha";
    case "DATE":
      return "Data";
    case "TEXT":
      return "Texto";
    case "IMAGE_UPLOAD":
      return "Fotos";
    default:
      return "";
  }
};

const NotAnswered = () => <span className="evDetail__badge evDetail__badge--none">Não respondido</span>;

const BooleanBadge: React.FC<{ value?: boolean | null }> = ({ value }) => {
  if (value === true)
    return (
      <span className="evDetail__badge evDetail__badge--yes">
        <EcoIcon name="check" size={12} />
        Sim
      </span>
    );
  if (value === false)
    return (
      <span className="evDetail__badge evDetail__badge--no">
        <EcoIcon name="x" size={12} />
        Não
      </span>
    );
  return <NotAnswered />;
};

const OtherAnswer: React.FC<{ answer: StoreEvaluationAnswer; token: string }> = ({ answer, token }) => {
  const q = answer.question as Question;
  switch (q.type) {
    case "SCALE":
      return hasValue(answer.intValue) ? (
        <span className="evDetail__badge evDetail__badge--value">
          {answer.intValue}
          {hasValue(q.maxValue) ? ` / ${q.maxValue}` : ""}
        </span>
      ) : (
        <NotAnswered />
      );
    case "DATE":
      return answer.dateValue ? (
        <span className="evDetail__badge evDetail__badge--value">{formatDate(answer.dateValue)}</span>
      ) : (
        <NotAnswered />
      );
    case "SINGLE_CHOICE": {
      const label = answer.option?.label ?? q.options?.find((o) => o.id === answer.optionId)?.label;
      return label ? (
        <span className="evDetail__chips">
          <span className="evDetail__chip">{label}</span>
        </span>
      ) : (
        <NotAnswered />
      );
    }
    case "MULTI_CHOICE": {
      const ids = Array.isArray(answer.optionIds) ? answer.optionIds : [];
      const chosen = (q.options ?? []).filter((o) => ids.includes(o.id));
      return chosen.length ? (
        <span className="evDetail__chips">
          {chosen.map((o) => (
            <span key={o.id} className="evDetail__chip">
              {o.label}
            </span>
          ))}
        </span>
      ) : (
        <NotAnswered />
      );
    }
    case "IMAGE_UPLOAD":
      return answer.images?.length ? (
        <span className="evDetail__answerPhotos">
          {answer.images.map((img) => (
            <AuthImage
              key={img.id}
              token={token}
              assetId={img.assetId}
              alt={q.prompt}
              className="evDetail__answerPhoto"
              statusClassName="evDetail__photoStatus"
            />
          ))}
        </span>
      ) : (
        <NotAnswered />
      );
    default:
      return <NotAnswered />;
  }
};

const Photo: React.FC<{ label: string; assetId?: string | null; at?: string | null; token: string }> = ({
  label,
  assetId,
  at,
  token,
}) => (
  <figure className="evDetail__figure">
    <div className="evDetail__photo">
      {assetId ? (
        <AuthImage token={token} assetId={assetId} alt={label} statusClassName="evDetail__photoStatus" />
      ) : (
        <span className="evDetail__photoStatus">
          <EcoIcon name="eyeOff" size={22} />
          Sem foto
        </span>
      )}
    </div>
    <figcaption className="evDetail__figcaption">
      <strong>{label}</strong>
      {at ? <span>{formatShortDateTime(at)}</span> : null}
    </figcaption>
  </figure>
);

export function StoreEvaluationDetailPage() {
  const { evaluationId } = useParams<{ evaluationId: string }>();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  // Mesma tela para o ADMIN (Operações) e para o supervisor (rota dele, desde 15/10/2026).
  const isAdmin = isAdminFromToken(token);
  const goBack = useGoBack(isAdmin ? STORE_EVALUATIONS_ADMIN_PAGE() : SUPERVISOR_HOME_PAGE());

  const [evaluation, setEvaluation] = useState<StoreEvaluation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
      return;
    }
    if (!evaluationId) return;

    let cancelled = false;
    setLoading(true);
    getStoreEvaluationByIdApi({ token, evaluationId })
      .then((data) => {
        if (!cancelled) setEvaluation(data);
      })
      .catch((err: any) => {
        if (!cancelled) toast.error(err?.message ?? "Erro ao carregar a avaliação.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, evaluationId, navigate]);

  const { booleanAnswers, otherAnswers } = useMemo(() => {
    const answered = (evaluation?.answers ?? [])
      .filter((a) => a.question)
      .sort((a, b) => (a.question!.order ?? 0) - (b.question!.order ?? 0));
    return {
      booleanAnswers: answered.filter((a) => a.question!.type === "BOOLEAN"),
      otherAnswers: answered.filter((a) => a.question!.type !== "BOOLEAN"),
    };
  }, [evaluation]);

  const storeName = evaluation?.store?.nomeFantasia ?? "Loja";
  const isSubmitted = evaluation?.status === "SUBMITTED";
  const totalQuestions = booleanAnswers.length + otherAnswers.length;
  const positives = booleanAnswers.filter((a) => a.booleanValue === true).length;
  const scoredCount = booleanAnswers.filter((a) => hasValue(a.booleanValue)).length;
  const score = hasValue(evaluation?.score01) ? Number(evaluation!.score01) : null;
  const formTitle = evaluation?.form?.title ?? evaluation?.formType?.name ?? "Formulário";
  const address = [evaluation?.store?.endereco, evaluation?.store?.estado].filter(Boolean).join(" · ");

  const statusPill = isSubmitted ? (
    <span className="evDetail__badge evDetail__badge--yes">
      <EcoIcon name="check" size={12} />
      Finalizada
    </span>
  ) : (
    <span className="evDetail__badge evDetail__badge--open">
      <EcoIcon name="clock" size={12} />
      Aberta
    </span>
  );

  return (
    <div className="evDetail">
      <main className="evDetail__main">
        <EcoPageHeader
          crumbs={[
            ...(isAdmin
              ? [
                  { label: "Início", onClick: () => navigate(HOMEPAGE()) },
                  { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
                  { label: "Avaliações de loja", onClick: () => navigate(STORE_EVALUATIONS_ADMIN_PAGE()) },
                ]
              : [{ label: "Início", onClick: () => navigate(SUPERVISOR_HOME_PAGE()) }]),
            {
              label: evaluation
                ? `${storeName}, ${formatDate(evaluation.referenceDate).slice(0, 5)}`
                : "Avaliação",
            },
          ]}
          title={evaluation ? storeName : "Avaliação de loja"}
          subtitle={
            evaluation ? (
              <span className="evDetail__subtitle">
                {statusPill}
                <span>
                  {formTitle}
                  {totalQuestions
                    ? ` · ${totalQuestions} ${totalQuestions === 1 ? "pergunta" : "perguntas"}, ${booleanAnswers.length} ${
                        booleanAnswers.length === 1 ? "entra" : "entram"
                      } na nota`
                    : ""}
                </span>
              </span>
            ) : undefined
          }
          onBack={goBack}
          backLabel="Voltar"
        />

        {loading ? (
          <p className="ecoEmpty" aria-live="polite">
            Carregando avaliação...
          </p>
        ) : !evaluation ? (
          <p className="ecoEmpty">Avaliação não encontrada.</p>
        ) : (
          <>
            <section className="ecoCard evDetail__summary" aria-label="Resumo">
              <dl className="evDetail__facts">
                <div>
                  <dt>Endereço</dt>
                  <dd>{address || "—"}</dd>
                </div>
                <div>
                  <dt>Respondida por</dt>
                  <dd>{evaluation.answeredBy?.nomeCompleto || "—"}</dd>
                </div>
                <div>
                  <dt>Data de referência</dt>
                  <dd>{formatDate(evaluation.referenceDate)}</dd>
                </div>
                <div>
                  <dt>Enviada em</dt>
                  <dd>{isSubmitted ? formatDateTime(evaluation.submittedAt) : "Ainda não enviada"}</dd>
                </div>
              </dl>

              <div className="evDetail__score">
                <span className="evDetail__scoreLabel">Nota</span>
                {score !== null ? (
                  <>
                    <div className="evDetail__scoreLine">
                      <span className={`evDetail__scoreValue evDetail__scoreValue--${scoreBand(score)}`}>
                        {(score * 100).toFixed(0)}%
                      </span>
                      {hasValue(evaluation.stars) ? (
                        <span className="evDetail__scoreStars">
                          {Number(evaluation.stars).toLocaleString("pt-BR", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}{" "}
                          de 5
                        </span>
                      ) : null}
                    </div>
                    <span className="evDetail__bar" aria-hidden="true">
                      <span
                        className={`evDetail__barFill evDetail__barFill--${scoreBand(score)}`}
                        style={{ width: `${Math.max(0, Math.min(1, score)) * 100}%` }}
                      />
                    </span>
                    <span className="evDetail__scoreNote">
                      {positives} de {scoredCount} {scoredCount === 1 ? "pergunta" : "perguntas"} de Sim ou Não{" "}
                      {positives === 1 ? "atendida" : "atendidas"}
                    </span>
                  </>
                ) : (
                  <span className="evDetail__scoreNote">Sem nota até finalizar</span>
                )}
              </div>
            </section>

            <div className="evDetail__grid">
              <section className="ecoCard evDetail__questions" aria-labelledby="evDetail-q">
                <div className="evDetail__cardHead">
                  <h2 id="evDetail-q">Questionário</h2>
                  <span>
                    {totalQuestions} {totalQuestions === 1 ? "pergunta" : "perguntas"}
                  </span>
                </div>

                {totalQuestions === 0 ? (
                  <div className="evDetail__emptyWrap">
                    <p className="ecoEmpty">Nenhuma resposta registrada — a avaliação ainda não foi enviada.</p>
                  </div>
                ) : null}

                {booleanAnswers.length ? (
                  <>
                    <h3 className="evDetail__group">Sim ou Não · entram na nota</h3>
                    <ol className="evDetail__list">
                      {booleanAnswers.map((a, i) => (
                        <li
                          key={a.questionId}
                          className={`evDetail__item${a.booleanValue === false ? " evDetail__item--no" : ""}`}
                        >
                          <span className="evDetail__num">{i + 1}</span>
                          <span className="evDetail__prompt">{a.question!.prompt}</span>
                          <span className="evDetail__answer">
                            <BooleanBadge value={a.booleanValue} />
                          </span>
                        </li>
                      ))}
                    </ol>
                  </>
                ) : null}

                {otherAnswers.length ? (
                  <>
                    <h3 className="evDetail__group">Outras respostas · não entram na nota</h3>
                    <ol className="evDetail__list">
                      {otherAnswers.map((a, i) => {
                        const isText = a.question!.type === "TEXT";
                        return (
                          <li key={a.questionId} className={`evDetail__item${isText ? " evDetail__item--block" : ""}`}>
                            <span className="evDetail__num">{booleanAnswers.length + i + 1}</span>
                            <span className="evDetail__promptWrap">
                              <span className="evDetail__prompt">{a.question!.prompt}</span>
                              <span className="evDetail__hint">{typeHint(a.question!)}</span>
                            </span>
                            <span className="evDetail__answer">
                              {isText ? (
                                a.textValue?.trim() ? (
                                  <p className="evDetail__text">{a.textValue}</p>
                                ) : (
                                  <NotAnswered />
                                )
                              ) : (
                                <OtherAnswer answer={a} token={token ?? ""} />
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  </>
                ) : null}
              </section>

              <aside className="ecoCard evDetail__photos" aria-labelledby="evDetail-f">
                <div className="evDetail__photosHead">
                  <h2 id="evDetail-f">Fotos da auditoria</h2>
                  <span>Tiradas ao chegar e ao sair da loja.</span>
                </div>
                <Photo
                  label="Foto de entrada"
                  assetId={evaluation.entryPhotoAssetId}
                  at={evaluation.entryPhotoAt}
                  token={token ?? ""}
                />
                <Photo
                  label="Foto de saída"
                  assetId={evaluation.exitPhotoAssetId}
                  at={evaluation.exitPhotoAt}
                  token={token ?? ""}
                />
              </aside>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
